import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  CREATE_BANNER_STEP_NAMES,
  CREATE_BANNER_STEPS,
  EMPTY_BANNER,
  ROUTE_TO_STEP,
  STEP_ROUTE_NAMES,
} from 'src/services/consts/banner/create.const'
import { canAccessCreateBannerStep } from 'src/services/helpers/banner/create.helper'

export const useNewBannerStore = defineStore('new-banner', () => {
  const route = useRoute()
  const router = useRouter()
  const bannerToEdit = ref({ ...EMPTY_BANNER })
  const serverFieldError = ref(null)

  const currentStep = computed(() => {
    const step = ROUTE_TO_STEP[route.name]
    if (!step || !canAccessCreateBannerStep(step, bannerToEdit.value)) {
      return CREATE_BANNER_STEP_NAMES.REGULATIONS
    }
    return step
  })

  const hasUnsavedWizardChanges = computed(() => {
    if (currentStep.value === CREATE_BANNER_STEP_NAMES.BANNER_CREATED) return false
    const banner = bannerToEdit.value
    return (
      currentStep.value !== CREATE_BANNER_STEP_NAMES.REGULATIONS ||
      !!banner.siteName ||
      !!banner.description ||
      !!banner.url ||
      !!banner.privacyPolicyUrl ||
      banner.aliasDomains.some((d) => d?.trim())
    )
  })

  function setBannerFields(fields) {
    bannerToEdit.value = { ...bannerToEdit.value, ...fields }
    if (serverFieldError.value && fields[serverFieldError.value.field] !== undefined) {
      serverFieldError.value = null
    }
  }

  function resetBanner() {
    bannerToEdit.value = { ...EMPTY_BANNER }
    serverFieldError.value = null
  }

  function resetState() {
    resetBanner()
  }

  function navigateNext() {
    const idx = CREATE_BANNER_STEPS.findIndex((s) => s.key === currentStep.value)
    if (idx === -1 || idx >= CREATE_BANNER_STEPS.length - 1) return
    const nextKey = CREATE_BANNER_STEPS[idx + 1].key
    router.push({ name: STEP_ROUTE_NAMES[nextKey] })
  }

  function navigateBack() {
    const idx = CREATE_BANNER_STEPS.findIndex((s) => s.key === currentStep.value)
    if (idx <= 0) return
    const prevKey = CREATE_BANNER_STEPS[idx - 1].key
    router.push({ name: STEP_ROUTE_NAMES[prevKey] })
  }

  return {
    bannerToEdit,
    currentStep,
    hasUnsavedWizardChanges,
    serverFieldError,
    setBannerFields,
    resetBanner,
    resetState,
    navigateNext,
    navigateBack,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNewBannerStore, import.meta.hot))
}
